import { Stack, Tooltip, Typography, styled } from '@ethings/ui-toolkit';
import { SxProps, unstable_styleFunctionSx as unstableStyleFunctionSx } from '@mui/system';
import { ComponentProps, FC, PropsWithChildren, ReactNode } from 'react';
import { useResponsiveText } from '../../hooks';
import { innerText } from '../../util';
import { OverflowTooltip } from '../tooltip';

interface Props extends PropsWithChildren {
  boxSx?: SxProps;
  className?: string;
  'data-testid'?: string;
  'data-testid-inner'?: string;
  label: ReactNode;
  reverse?: boolean;
  sx?: SxProps;
  tooltipSlotProps?: ComponentProps<typeof Tooltip>['slotProps'];
  width?: number | string;
  wordWrap?: boolean;
  wrap?: boolean;
  deleted?: boolean;
}

const StyledStack = styled(Stack)<{ deleted: boolean; mergedSx?: SxProps }>(({ deleted, mergedSx, theme }) => ({
  textDecoration: deleted ? 'line-through!important' : 'none',
  userSelect: 'text',
  ...(unstableStyleFunctionSx({ sx: mergedSx, theme } as never) as Record<string, unknown>),
}));

export const ListPaperColumn: FC<Props> = ({ boxSx, children, className, label: labelContent, reverse, sx, tooltipSlotProps, width, deleted = false, wrap = false, wordWrap, ...props }) => {
  const labelLength = innerText(labelContent ?? '').length;

  const labelStyles = useResponsiveText('0.75rem', labelLength, {
    minFontSize: '0.65rem',
    maxFontSize: '0.75rem',
    textLengthThreshold: 20,
  });

  const mergedStyles = { ...boxSx, ...sx };
  return <StyledStack data-testid={props['data-testid']} className={`ListPaperColumn-root ${className || ''}`} flexDirection={reverse ? 'column-reverse' : 'column'} deleted={deleted} mergedSx={mergedStyles}>
    <Typography
      component="label"
      variant='caption'
      color='text.secondary'
      overflow={wrap ? 'visible' : 'hidden'}
      textOverflow='ellipsis'
      whiteSpace={wrap ? 'normal' : 'nowrap'}
      style={{
        fontSize: labelStyles.fontSize,
        lineHeight: labelStyles.lineHeight,
        wordBreak: wrap ? 'break-word' : labelStyles.wordBreak,
      }}
    >
      {labelContent}
    </Typography>
    <OverflowTooltip
      data-testid={props['data-testid-inner']}
      deleted={deleted}
      sx={{
        whiteSpace: wrap ? 'normal' : 'nowrap',
        wordWrap: wordWrap ? 'break-word' : 'normal',
        textOverflow: wrap ? 'clip' : 'ellipsis',
      }}
      tooltipSlotProps={tooltipSlotProps}
    >
      {children}
    </OverflowTooltip>
  </StyledStack>;
};
